import { Component, Input } from '@angular/core';
import { AumicoStyleOptions, ICellFormat } from '@modeso/types__aumico-lib-core-be';
import { RowTypes } from '../../../../../../aumico-lib-annualreport-fe/src/public-api';
import { FIRST_ELEMENT_INDEX } from '../../../../../../aumico-lib-core-fe/src/lib';
import { SuperTableHelper } from '../../../helpers/super-table.helper';
import { INotesSuperTableColumnForPrintPreview } from '../../../interfaces/notes-super-table-column-for-print-preview.interface';
import { INotesSuperTableDataForPrintPreview } from '../../../interfaces/notes-super-table-data-for-print-preview.interface';
import { INotesSuperTableRowForPrintPreview } from '../../../interfaces/notes-super-table-row-for-print-preview.interface';
import { INotesSuperTableSegmentForPrintPreview } from '../../../interfaces/notes-super-table-segment-for-print-preview.interface';
import {
    TransposedINotesSuperTableColumnForPrintPreview,
    TransposedSuperTableColumnType,
} from '../../notes-super-table-display/notes-super-table-display.component';

const BLACK_TEXT_COLOR = '#38323C';
const PRIMARY_HIGHLIGHT_COLOR = '#ECFFFE';

@Component({
    selector: 'super-table-notes-preview',
    templateUrl: './super-table-notes-preview.component.html',
    styleUrls: ['./super-table-notes-preview.component.scss'],
})
export class SuperTableNotesPreviewComponent {
    TransposedSuperTableColumnType = TransposedSuperTableColumnType;
    textColor = BLACK_TEXT_COLOR;
    backgroundColor = PRIMARY_HIGHLIGHT_COLOR;
    tableFooterColor: string;
    tableHeaderColor: string;
    superTableRows: INotesSuperTableRowForPrintPreview[] = [];
    transposedRows: INotesSuperTableRowForPrintPreview[] = [];
    transposedColumns: TransposedINotesSuperTableColumnForPrintPreview[] = [];
    superTableColumns: INotesSuperTableColumnForPrintPreview[] = [];
    transpose: boolean = false;
    hasSegments: boolean = false;
    tableTitle: string = 'false';
    componentTitle: string | undefined;
    private superTableSegments: INotesSuperTableSegmentForPrintPreview[] = [];
    allSegmentsChildColumnIds: string[] = [];
    startingColumnIdToSegment: Map<string, INotesSuperTableSegmentForPrintPreview> = new Map<
        string,
        INotesSuperTableSegmentForPrintPreview
    >();

    @Input() set sectionNotes(value: INotesSuperTableDataForPrintPreview | undefined | string) {
        if (value == null || value === '') {
            return;
        }
        this.superTableRows = (value as INotesSuperTableDataForPrintPreview).rows;
        this.superTableColumns = (value as INotesSuperTableDataForPrintPreview).columns;

        this.superTableSegments = (value as INotesSuperTableDataForPrintPreview).segments;
        this.transpose = (value as INotesSuperTableDataForPrintPreview).transpose;
        this.componentTitle = (value as INotesSuperTableDataForPrintPreview).componentTitle;
        this.prepareStartingColumnIdToSegmentMap();
    }

    @Input() colored: boolean = false;

    @Input() highlightTodos = false;

    @Input() tableNumber: string | undefined;

    constructor(private readonly superTableHelper: SuperTableHelper) {}

    private prepareStartingColumnIdToSegmentMap(): void {
        for (const segment of this.superTableSegments) {
            this.startingColumnIdToSegment.set(segment.childColumnsIds[FIRST_ELEMENT_INDEX], {
                ...segment,
                startColumnId: segment.childColumnsIds[FIRST_ELEMENT_INDEX],
            });
            this.allSegmentsChildColumnIds.push(...segment.childColumnsIds);
        }

        this.hasSegments = this.superTableSegments.length > 0;
        if (this.transpose) {
            this.tableTitle = this.superTableColumns[0].columnTitle;
            this.superTableColumns.shift();
            this.transposedRows = this.transposeRows();
            this.transposedColumns = this.transposeColumns();
        }
    }

    transposeRows(): INotesSuperTableRowForPrintPreview[] {
        const transposedRows: INotesSuperTableRowForPrintPreview[] = [];
        const maxColumns = this.superTableColumns.length;
        const maxRowNumber = this.superTableRows.length;

        for (let i = 0; i < maxColumns; i++) {
            let rowType = RowTypes.ROW;
            if (this.superTableColumns[i].isRatio) {
                rowType = RowTypes.CALC_ROW;
            }
            const transposedRow: INotesSuperTableRowForPrintPreview = {
                rowType: rowType,
                t: this.superTableColumns[i].columnTitle,
                format: this.superTableColumns[i].format,
            };

            for (let j = 0; j < maxRowNumber; j++) {
                const columnId = this.superTableColumns[i].columnId;
                const row = this.superTableRows[j];
                if (this.superTableColumns[i].isRatio) {
                    transposedRow['row' + j.toString()] = row[columnId] + ' %';
                } else {
                    transposedRow['row' + j.toString()] = row[columnId];
                }
            }
            transposedRows.push(transposedRow);
        }

        return transposedRows;
    }

    transposeColumns(): TransposedINotesSuperTableColumnForPrintPreview[] {
        const transposedCols: TransposedINotesSuperTableColumnForPrintPreview[] = [];
        const maxRowNumber = this.superTableRows.length;

        for (let i = 0; i < maxRowNumber; i++) {
            const rowType = this.superTableRows[i].rowType;
            let type = TransposedSuperTableColumnType.NUMBER;
            if (rowType === RowTypes.TITLE_ROW) {
                type = TransposedSuperTableColumnType.EMPTY;
            }
            if (rowType === RowTypes.EMPTY_ROW) {
                type = TransposedSuperTableColumnType.EMPTY;
            }
            const transposedCol: TransposedINotesSuperTableColumnForPrintPreview = {
                columnId: 'row' + i.toString(),
                columnTitle: '',
                type,
                isRatio: false,
                format: this.superTableRows[i].format,
            };

            transposedCols.push(transposedCol);
        }

        return transposedCols;
    }

    resolveEffectiveCellStyles(rowFormat?: ICellFormat, colFormat?: ICellFormat): AumicoStyleOptions[] {
        return this.superTableHelper.resolveEffectiveCellStyles(rowFormat, colFormat);
    }

    resolveEffectiveDecimalPlaces(rowFormat?: ICellFormat, colFormat?: ICellFormat): number | undefined {
        return this.superTableHelper.resolveEffectiveDecimalPlaces(rowFormat, colFormat);
    }
}
